import React, { Component } from 'react';
import isEqual from 'lodash/isEqual';
import find from 'lodash/find';
import cloneDeep from 'lodash/cloneDeep';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';

import {
  fetchJobs,
  clearJobs,
  updateJob,
  cloneJob,
  rerunJob,
  removeJob,
  setJobsTabUpdate,
  clearJobSettings,
} from 'store/actions/jobs';
import ExportStatusBar from './ExportStatusBar';
import ExportJobsList from './ExportJobsList';
import SlateDialog from '../Shared/SlateDialog/SlateDialog';
import Preloader from '../Preloader/Preloader';
import { route, jobsTabs, jobsStatuses } from 'config/constants';
import { createReplacePatch } from 'helpers/patchHelper';
import FormSlateViewTimeZone from '../../containers/Forms/FormSlateViewTimeZone';
import './style.css';

const API_CALL_INTERVAL = 1000;

class ExportJobs extends Component {
  constructor(props) {
    super(props);
    this.state = {
      currentTab: jobsTabs.ACTIVE,
      activeJobs: props.jobs[jobsTabs.ACTIVE],
      favoritesJobs: props.jobs[jobsTabs.FAVORITES],
      archivedJobs: props.jobs[jobsTabs.ARCHIVED],
      // Cache counts separately so they persist during tab switches
      cachedCounts: {
        [jobsTabs.ACTIVE]: null,
        [jobsTabs.FAVORITES]: null,
        [jobsTabs.ARCHIVED]: null,
      },
      currentJob: undefined,
      isRerunDialogOpen: false,
      isCloneDialogOpen: false,
      isRemoveDialogOpen: false,
      isArchiveDialogOpen: false,
    };
  }

  UNSAFE_componentWillMount = () => {
    const { currentAccount, fetchJobs } = this.props;
    if (currentAccount) {
      fetchJobs(jobsTabs.ACTIVE);
    }
  };

  UNSAFE_componentWillReceiveProps = (nextProps) => {
    const { currentAccount, jobs, fetchJobs, setJobsTabUpdate } = this.props;
    const { currentTab } = this.state;

    if (nextProps.currentAccount) {
      if (
        currentAccount &&
        currentAccount.uuid !== nextProps.currentAccount.uuid
      ) {
        fetchJobs(currentTab);
      }
      if (!currentAccount) {
        fetchJobs(currentTab);
      }
    }

    if (!isEqual(jobs, nextProps.jobs)) {
      const newState = {
        activeJobs: nextProps.jobs[jobsTabs.ACTIVE],
        favoritesJobs: nextProps.jobs[jobsTabs.FAVORITES],
        archivedJobs: nextProps.jobs[jobsTabs.ARCHIVED],
      };

      // Update cached counts for any tabs that have data
      const { cachedCounts } = this.state;
      const newCachedCounts = { ...cachedCounts };
      if (nextProps.jobs[jobsTabs.ACTIVE]) {
        newCachedCounts[jobsTabs.ACTIVE] = nextProps.jobs[jobsTabs.ACTIVE].length;
      }
      if (nextProps.jobs[jobsTabs.FAVORITES]) {
        newCachedCounts[jobsTabs.FAVORITES] = nextProps.jobs[jobsTabs.FAVORITES].length;
      }
      if (nextProps.jobs[jobsTabs.ARCHIVED]) {
        newCachedCounts[jobsTabs.ARCHIVED] = nextProps.jobs[jobsTabs.ARCHIVED].length;
      }
      newState.cachedCounts = newCachedCounts;

      this.setState(newState);

      if (this.isUnfinishedJobExist(nextProps.jobs[currentTab])) {
        if (!this.interval) {
          this.interval = setInterval(this.handleFetchJobs, API_CALL_INTERVAL);
        }
      } else {
        if (this.interval) {
          clearInterval(this.interval);
          this.interval = undefined;
        }
      }
    }

    if (nextProps.needUpdateTab) {
      setJobsTabUpdate();
      this.handleChangeTab(nextProps.needUpdateTab);
    }
  };

  componentWillUnmount = () => {
    this.props.clearJobs();
    if (this.interval) {
      clearInterval(this.interval);
    }
  };

  getJobCounts = () => {
    const { activeJobs, favoritesJobs, archivedJobs, cachedCounts } = this.state;
    // Use current data if available, otherwise fall back to cached counts
    return {
      [jobsTabs.ACTIVE]: activeJobs ? activeJobs.length : cachedCounts[jobsTabs.ACTIVE],
      [jobsTabs.FAVORITES]: favoritesJobs ? favoritesJobs.length : cachedCounts[jobsTabs.FAVORITES],
      [jobsTabs.ARCHIVED]: archivedJobs ? archivedJobs.length : cachedCounts[jobsTabs.ARCHIVED],
    };
  };

  getCurrentJobs = () => {
    const { currentTab, activeJobs, favoritesJobs, archivedJobs } = this.state;
    switch (currentTab) {
      case jobsTabs.ACTIVE:
        return activeJobs;
      case jobsTabs.FAVORITES:
        return favoritesJobs;
      case jobsTabs.ARCHIVED:
        return archivedJobs;
      default:
        return null;
    }
  };

  render() {
    const {
      currentTab,
      currentJob,
      isRerunDialogOpen,
      isCloneDialogOpen,
      isRemoveDialogOpen,
      isArchiveDialogOpen,
    } = this.state;
    const { t } = this.props;

    const jobs = this.getCurrentJobs();
    const counts = this.getJobCounts();

    return (
      <div className="inner-page export-page">
        <div className="export-header">
          <h1 className="export-header-title">{t('export:exportJobs')}</h1>
          <button
            type="button"
            className="export-header-action"
            onClick={this.createNewJob}
          >
            <span className="material-symbols-outlined">add</span>
            {t('export:createNewExportJob')}
          </button>
        </div>

        <div className="export-content">
          <ExportStatusBar
            currentTab={currentTab}
            counts={counts}
            onTabChange={this.handleChangeTab}
            t={t}
          />

          <div className="export-tab-content" key={currentTab}>
            {jobs ? (
              <ExportJobsList
                jobs={jobs}
                tab={currentTab}
                onRerunClick={this.handleOpenRerunDialog}
                onCloneClick={this.handleOpenCloneDialog}
                onFavoriteClick={this.handleToggleFavorite}
                onRemoveClick={
                  currentTab === jobsTabs.ARCHIVED
                    ? this.handleOpenRemoveDialog
                    : this.handleOpenArchiveDialog
                }
                onCreateNew={this.createNewJob}
                t={t}
              />
            ) : (
              <div className="export-loading">
                <Preloader />
              </div>
            )}
          </div>
        </div>

        <SlateDialog
          title={t('export:rerunExportJob')}
          open={isRerunDialogOpen}
          onClose={this.handleCloseDialog}
          maxWidth="xs"
          actions={[
            {
              label: t('export:cancel'),
              onClick: this.handleCloseDialog,
              variant: 'secondary',
            },
            {
              label: t('export:rerunJobNow'),
              onClick: () => this.handleRerunJob(currentJob),
              variant: 'primary',
            },
          ]}
        >
          <p className="export-dialog-message">
            {t('export:rerunTheExportJob')}{' '}
            <span className="export-dialog-job-name">
              {currentJob && currentJob.name}
            </span>?
          </p>
          <div className="export-dialog-timezone-section">
            <label className="export-dialog-label">Timezone</label>
            <p className="export-dialog-hint">
              All dates and times will be exported in your selected timezone.
            </p>
            <FormSlateViewTimeZone hideWarning />
          </div>
        </SlateDialog>

        <SlateDialog
          title={t('export:copyToNewExportJob')}
          open={isCloneDialogOpen}
          onClose={this.handleCloseDialog}
          maxWidth="xs"
          actions={[
            {
              label: t('export:cancel'),
              onClick: this.handleCloseDialog,
              variant: 'secondary',
            },
            {
              label: t('export:copyToNewJob'),
              onClick: () => this.handleCloneJob(currentJob),
              variant: 'primary',
            },
          ]}
        >
          <p className="export-dialog-message">
            {t('export:copyExportJob')}{' '}
            <span className="export-dialog-job-name">
              {currentJob && currentJob.name}
            </span>{' '}
            {t('export:youWillBeAbleToCustomize')}
          </p>
        </SlateDialog>

        <SlateDialog
          title={`${t('export:archiveExportJob')}?`}
          open={isArchiveDialogOpen}
          onClose={this.handleCloseDialog}
          maxWidth="xs"
          actions={[
            {
              label: t('export:cancel'),
              onClick: this.handleCloseDialog,
              variant: 'secondary',
            },
            {
              label: t('export:archiveExportJob'),
              onClick: () => this.handleArchiveJob(currentJob),
              variant: 'destructive',
            },
          ]}
        >
          <p className="export-dialog-message">
            {t('export:archiveTheExportJob')}{' '}
            <span className="export-dialog-job-name">
              {currentJob && currentJob.name}
            </span>{' '}
            {t('export:fromRecentAndFavorites')}
          </p>
        </SlateDialog>

        <SlateDialog
          title={`${t('export:deleteExportJob')}?`}
          open={isRemoveDialogOpen}
          onClose={this.handleCloseDialog}
          maxWidth="xs"
          actions={[
            {
              label: t('export:cancel'),
              onClick: this.handleCloseDialog,
              variant: 'secondary',
            },
            {
              label: t('export:deleteExportJob'),
              onClick: () => this.handleRemoveJob(currentJob),
              variant: 'destructive',
            },
          ]}
        >
          <p className="export-dialog-message">
            {t('export:permanentlyDeleteExportJob')}{' '}
            <span className="export-dialog-job-name">
              {currentJob && currentJob.name}
            </span>
            ?
          </p>
          <p className="export-dialog-warning">
            {t('export:thisCannotBeUndone')}
          </p>
        </SlateDialog>
      </div>
    );
  }

  isUnfinishedJobExist = (jobs) => {
    if (jobs) {
      const unfinished = jobs.filter(
        (job) => job.status !== jobsStatuses.FINISHED,
      );
      return !!unfinished.length;
    }
  };

  handleFetchJobs = () => {
    this.props.fetchJobs(this.state.currentTab);
  };

  handleChangeTab = (value) => {
    this.props.clearJobs();
    this.setState(
      {
        currentTab: value,
      },
      () => {
        this.props.fetchJobs(value);
      },
    );
  };

  handleOpenRerunDialog = (job) => {
    this.setState({
      isRerunDialogOpen: true,
      currentJob: job,
    });
  };

  handleOpenCloneDialog = (job) => {
    this.setState({
      isCloneDialogOpen: true,
      currentJob: job,
    });
  };

  handleOpenRemoveDialog = (job) => {
    this.setState({
      isRemoveDialogOpen: true,
      currentJob: job,
    });
  };

  handleOpenArchiveDialog = (job) => {
    this.setState({
      isArchiveDialogOpen: true,
      currentJob: job,
    });
  };

  handleCloseDialog = () => {
    this.setState({
      isRerunDialogOpen: false,
      isCloneDialogOpen: false,
      isRemoveDialogOpen: false,
      isArchiveDialogOpen: false,
      currentJob: undefined,
    });
  };

  createNewJob = () => {
    this.props.clearJobSettings();
    this.props.history.push({
      pathname: route.EXPORT_NEW_JOB,
    });
  };

  handleRerunJob = (job) => {
    this.props.rerunJob(job.uuid, job.name);
    this.handleCloseDialog();
  };

  handleCloneJob = (job) => {
    this.props.cloneJob(job.uuid);
  };

  handleToggleFavorite = (job) => {
    const { activeJobs, favoritesJobs, archivedJobs, currentTab } = this.state;
    const { updateJob } = this.props;
    let currentJob;
    let jobs;

    switch (currentTab) {
      case jobsTabs.ACTIVE: {
        jobs = cloneDeep(activeJobs);
        currentJob = find(jobs, (item) => item.uuid === job.uuid);
        currentJob.isFavorite = !currentJob.isFavorite;
        this.setState({ activeJobs: jobs });
        break;
      }
      case jobsTabs.FAVORITES: {
        jobs = cloneDeep(favoritesJobs);
        currentJob = find(jobs, (item) => item.uuid === job.uuid);
        currentJob.isFavorite = !currentJob.isFavorite;
        jobs = jobs.filter((item) => item.uuid !== job.uuid);
        this.setState({ favoritesJobs: jobs });
        break;
      }
      case jobsTabs.ARCHIVED: {
        jobs = cloneDeep(archivedJobs);
        currentJob = find(jobs, (item) => item.uuid === job.uuid);
        currentJob.isFavorite = !currentJob.isFavorite;
        jobs = jobs.filter((item) => item.uuid !== job.uuid);
        this.setState({ archivedJobs: jobs });
        break;
      }
      default: {
        break;
      }
    }

    const data = {};
    data.patches = [createReplacePatch(`/isFavorite`, currentJob.isFavorite)];
    updateJob(data, currentJob.uuid);
  };

  handleArchiveJob = (job) => {
    const { activeJobs, favoritesJobs, currentTab } = this.state;
    const { updateJob } = this.props;
    let jobs;

    switch (currentTab) {
      case jobsTabs.ACTIVE: {
        jobs = cloneDeep(activeJobs);
        jobs = jobs.filter((item) => item.uuid !== job.uuid);
        this.setState({ activeJobs: jobs });
        break;
      }
      case jobsTabs.FAVORITES: {
        jobs = cloneDeep(favoritesJobs);
        jobs = jobs.filter((item) => item.uuid !== job.uuid);
        this.setState({ favoritesJobs: jobs });
        break;
      }
      default: {
        break;
      }
    }

    const data = {};
    data.patches = [createReplacePatch(`/isArchived`, true)];
    updateJob(data, job.uuid);

    this.handleCloseDialog();
  };

  handleRemoveJob = (job) => {
    this.handleCloseDialog();
    this.props.removeJob(job.uuid, job.name);
  };
}

const mapStateToProps = (state) => ({
  currentAccount: state.accounts.currentAccount,
  jobs: state.jobs.list,
  needUpdateTab: state.jobs.needUpdateTab,
});

export default withTranslation()(
  connect(mapStateToProps, {
    fetchJobs,
    clearJobs,
    updateJob,
    cloneJob,
    rerunJob,
    removeJob,
    setJobsTabUpdate,
    clearJobSettings,
  })(ExportJobs),
);
